import HasID from "./HasID";
import { Note, NoteActivityType } from "./Note";
import TrackedObject from "./TrackedObject";
import TextUtils from "@/util/TextUtils";
import { Prescription } from "./Prescription";
import { DrugSource } from "./Drug/DrugProduct";

export interface PrescriberFollowUpCase extends HasID, TrackedObject {
    prescriberId: number;
    patientId: number;
    documentId: number;
    packageID: number;
    drugSource: DrugSource;
    type: FaxType;
    storeID: number;
    rxNumber: number;
    rfNumber: number;
    status: PrescriberFollowUpStatus;
    statusUpdated: Date;

    patientName: string;
    prescriberName: string;
    prescriberPhone: string;
    storeName: string;
    drugName: string;
    drugNdc?: string;
    notes?: Array<Note>;

    notifiedToPatient: Date;
    notifiedToPatientBy: string;

    faxSent: Date;
    faxSentBy: string;

    outreachType?: NoteActivityType;
    attemptCount: number;
    programId?: number;

    nextTouchDate?: Date;
    followUpTypeName?: string;
    lockedBy?: string;
    lockExpires?: string;

    /** When set, this case was sent to and is managed by a partner (remote) pharmacy instance. It is a
     * read-only tracking mirror locally; resolution happens on the partner instance. */
    remoteInstanceCaseId?: number;
}

export enum FaxType {
    RxExternalTransfer,
    RefillAuthorization,
    RxClarification,
    NewRxRequest,
    UnfilledNDC,
    AlternateRxRequest,
    RxTransferIn,
    EscriptRxRenewalRequest,
    CustomPrescriberTemplate,
    CustomPriorAuthTemplate,
    UserTaskCompletionForm,
    UserTaskSummary,
}

export enum PrescriberFollowUpStatus {
    Created, // Newly Created
    InProgress, // No response for now, held until the configured next touch date
    NotResponded, //Definitely not responded
    Responded, // Successfully responded by doctor
    Canceled, // Closes with no communication to patient or prescriber: opened by mistake,
    // or superseded by a matching Rx/renewal received through another channel
    Denied // Prescriber refused the request - never contacts the prescriber
}

/**
 * Background workers stamp their own service name into UpdatedBy / FaxSentBy / NotifiedToPatientBy,
 * which reads as a person to anyone looking at the case. Map those to a readable system label so a
 * case the system closed on its own is recognizable as such.
 */
const SYSTEM_ACTOR_LABELS: Record<string, string> = {
    AutocloserDoctorFollowUpWorker: "System (auto-closed)",
    FollowUpCaseAutoCancelService: "System (auto-cancelled)",
    RefillRequestBatchWorker: "System (refill batch)",
};

export function displayFollowUpActor(actor?: string | null): string {
    if (!actor) return "Unknown";
    return SYSTEM_ACTOR_LABELS[actor] ?? actor;
}

/**
 * Statuses that keep a case in the queue. Mirrors PrescriberFollowUpCase.IsClosedStatus in C# and
 * the Status IN (0, 1) predicate in SQL — the open pair is Created and InProgress, everything else
 * is terminal.
 */
export function isOpenFollowUpStatus(status?: PrescriberFollowUpStatus | null): boolean {
    return status === PrescriberFollowUpStatus.Created || status === PrescriberFollowUpStatus.InProgress;
}

/**
 * Days a case waits when neither the program nor the tenant configures a next touch for its status.
 * Mirrors the ISNULL fallback in fn_DoctorFollowUpNextTouchDays, which is where the value is actually
 * applied; this copy exists so the Configuration editor can name the number a blank field will get
 * rather than implying some other one.
 */
export const DEFAULT_NEXT_TOUCH_DAYS = 3;

/**
 * Request types the Doctor Follow-Up queue withholds from its Request Type picker. Cases of these
 * types are still created and still appear in the queue — only the filter option is withheld, so the
 * enum values themselves must stay intact for the Status and Request columns to render.
 *
 * Shared with the filter-restore path rather than written inline in the template: a saved filter has
 * to be checked against the same list the picker builds from.
 */
export const QUEUE_FILTER_EXCLUDED_FAX_TYPES: FaxType[] = [
    FaxType.RxExternalTransfer,
    FaxType.UnfilledNDC,
    FaxType.RxTransferIn,
];

/**
 * Narrows a restored Request Type filter to a value the picker can actually display.
 *
 * A filter saved while a type was still offered outlives the decision to withhold it, and the stale
 * value is worse than useless: the queue comes back filtered, but the select has no matching option
 * so it renders blank — and with nothing selected it also hides its clear button, leaving no obvious
 * way back to an unfiltered queue. Dropping the value restores "no filter", which is what an empty
 * control already claims. Also rejects non-numeric and out-of-range values, since the saved payload
 * is whatever an older build happened to write.
 */
export function sanitizeQueueFaxTypeFilter(value: unknown): FaxType | null {
    if (typeof value !== 'number' || !(value in FaxType)) return null;
    return QUEUE_FILTER_EXCLUDED_FAX_TYPES.includes(value) ? null : value;
}

export class PrescriberFollowUpCase {
    constructor(obj?: PrescriberFollowUpCase) {
        this.notes = new Array<Note>();
        if (obj) {
            Object.assign(this, obj);
            this.notes = obj.notes?.map(n => new Note(n));
        }
    }

    get statusName(): string {
        return PrescriberFollowUpStatus[this.status];
    }

    get requestName(): string {
        if (this.type === FaxType.CustomPrescriberTemplate && this.followUpTypeName) {
            return this.followUpTypeName;
        }
        return TextUtils.camelCaseToNormal(FaxType[this.type]);
    }

    get rxID(): string {
        if (!this.rxNumber) return '';
        return Prescription.createRxId(this.storeID, this.rxNumber, this.rfNumber);
    }
}

export class GroupOfCasesByDoctor {
    prescriberID = 0;
    prescriberName: string;
    cases: Array<PrescriberFollowUpCase> = new Array<PrescriberFollowUpCase>();

    constructor(prescriberID?: number, prescriberName?: string, cases?: Array<PrescriberFollowUpCase>) {
        this.prescriberID = prescriberID || 0;
        this.prescriberName = prescriberName || '';
        this.cases = cases || new Array<PrescriberFollowUpCase>();
    }
}
